import { EditOutlined } from "@ant-design/icons";
import { Button, Form, Input, Modal, Typography } from "antd";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { onModalOk } from "@/shared/lib";

import { useAdjustBalance } from "../model/useAdjustBalance";

interface Values {
  amount: string;
  comment: string;
}

/**
 * Ручная корректировка баланса.
 *
 * Поле — текст, а не `InputNumber`: сумма обязана дойти до бэкенда строкой
 * (правило ТЗ №3), а `InputNumber` отдаёт `number` и на больших значениях
 * теряет копейки ещё до отправки.
 */
export function AdjustBalanceButton({ userId }: { userId: number }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [form] = Form.useForm<Values>();
  const mutation = useAdjustBalance(userId);

  return (
    <>
      <Button
        icon={<EditOutlined aria-hidden />}
        onClick={() => {
          setOpen(true);
        }}
      >
        {t("users.adjust")}
      </Button>

      <Modal
        open={open}
        title={t("users.adjust")}
        okText={t("common.save")}
        cancelText={t("common.cancel")}
        okButtonProps={{ loading: mutation.isPending }}
        onCancel={() => {
          setOpen(false);
        }}
        onOk={onModalOk(form, async (values) => {
          await mutation.mutateAsync(values);
          form.resetFields();
          setOpen(false);
        })}
      >
        <Typography.Paragraph type="secondary">{t("users.adjustHint")}</Typography.Paragraph>
        <Form form={form} layout="vertical">
          <Form.Item
            name="amount"
            label={t("users.adjustAmount")}
            rules={[
              { required: true, message: t("login.required") },
              // Тот же шаблон, что принимает бэкенд: до двух знаков, знак минус
              // разрешён. Пропустить «100,50» нельзя — запятую сервер отвергнет.
              { pattern: /^-?\d+(\.\d{1,2})?$/, message: "Например: -100.50" },
            ]}
          >
            <Input autoFocus inputMode="decimal" placeholder="-100.00" />
          </Form.Item>
          <Form.Item
            name="comment"
            label={t("common.comment")}
            rules={[{ required: true, min: 3, message: t("common.commentRequired") }]}
          >
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
